// LLM settings — scoped to the current login session.
//
// Security stance: API keys and provider secrets live in sessionStorage
// (see Chat.tsx → CHAT_SETTINGS_KEY) so they stay usable for one browser
// session — across tab switches and reloads — and are wiped when:
//   - the user signs out (auth.signOut clears the chat key),
//   - the browser tab is closed.
// sessionStorage is per-tab and is never synced to other devices, unlike
// localStorage.
//
// Any older browser session that persisted the key under the legacy
// "cs.llm.settings" key is purged on module load via `purgeLegacy()`.
//
// Production stance is unchanged: production deployments proxy chat
// traffic through an Azure backend with managed identity. Browser-direct
// keys are a demo pattern only.

import type { LlmSettings } from "./types";

const LEGACY_KEY = "cs.llm.settings";

/** Defensively wipe any prior persisted settings — runs once on import. */
function purgeLegacy(): void {
  try {
    localStorage.removeItem(LEGACY_KEY);
    sessionStorage.removeItem(LEGACY_KEY);
  } catch {
    // localStorage may be disabled — silently no-op.
  }
}
purgeLegacy();

/** True when the user has provided a usable provider+key+model triple. */
export function isConfigured(s: LlmSettings | null): s is LlmSettings {
  return !!s && !!s.apiKey.trim() && !!s.model.trim();
}
